import React, { useState, useEffect, useRef, useContext } from "react";

import * as Yup from "yup";
import { Formik, FieldArray, Form } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CircularProgress from "@material-ui/core/CircularProgress";
import Avatar from "@material-ui/core/Avatar";
import CameraAltIcon from "@material-ui/icons/CameraAlt";
import Box from "@material-ui/core/Box";

import { i18n } from "../../translate/i18n";

import api, { getProfilePicUrl } from "../../services/api";
import toastError from "../../errors/toastError";
import { getInitials } from "../../helpers/getInitials";
import { generateColor } from "../../helpers/colorGenerator";
import ImageCropModal from "../ImageCropModal";
import ContactNotesStandalone from "../ContactNotesStandalone"; // ✅ NUEVO: Importar componente de observaciones
import { SocketContext } from "../../context/Socket/SocketContext"; // ✅ FIX 05/12/2025: Importar SocketContext para escuchar actualizaciones en tiempo real

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	textField: {
		marginRight: theme.spacing(1),
		flex: 1,
	},

	extraAttr: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	// ✅ NUEVO: Estilos para avatar
	avatarContainer: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		marginBottom: theme.spacing(2),
		marginTop: theme.spacing(1),
	},
	avatar: {
		width: 100,
		height: 100,
		fontSize: "2.5rem",
		fontWeight: "bold",
		minWidth: 100,
		minHeight: 100,
		"& img": {
			objectFit: "cover",
			width: "100%",
			height: "100%",
		},
	},
	avatarButton: {
		position: "relative",
		marginTop: theme.spacing(1),
	},
	cameraIcon: {
		position: "absolute",
		bottom: 0,
		right: 0,
		backgroundColor: theme.palette.primary.main,
		color: "white",
		padding: "4px",
		borderRadius: "50%",
		"&:hover": {
			backgroundColor: theme.palette.primary.dark,
		},
	},
	hiddenInput: {
		display: "none",
	},
}));

const ContactSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Too Short!")
		.max(50, "Too Long!")
		.required("Required"),
	number: Yup.string().min(8, "Too Short!").max(50, "Too Long!"),
	email: Yup.string().email("Invalid email"),
});

const ContactModal = ({ open, onClose, contactId, initialValues, onSave }) => {
	const classes = useStyles();
	const isMounted = useRef(true);
	const socketManager = useContext(SocketContext); // ✅ FIX 05/12/2025: Obtener socketManager para escuchar eventos

	const initialState = {
		name: "",
		number: "",
		email: "",
	};

	const [contact, setContact] = useState(initialState);
	const [uploadingPicture, setUploadingPicture] = useState(false);
	const [cropModalOpen, setCropModalOpen] = useState(false);
	const [selectedImage, setSelectedImage] = useState(null);
	const [avatarKey, setAvatarKey] = useState(0); // ✅ NUEVO: Key para forzar re-render del avatar
	const fileInputRef = useRef(null);

	useEffect(() => {
		isMounted.current = true;
		return () => {
			isMounted.current = false;
		};
	}, []);

	useEffect(() => {
		const fetchContact = async () => {
			if (initialValues) {
				setContact(prevState => {
					return { ...prevState, ...initialValues };
				});
			}

			if (!contactId || !open) return; // ✅ Solo cargar si el modal está abierto

			try {
				// ✅ CORRECCIÓN: Agregar timestamp y parámetro para evitar cache del backend
				const { data } = await api.get(`/contacts/${contactId}`, {
					params: { 
						_t: Date.now(),
						_nocache: true 
					}
				});
				console.log("🔍 ContactModal: Datos recibidos de API:", {
					id: data.id,
					name: data.name,
					extraInfoCount: data.extraInfo?.length || 0,
					extraInfo: data.extraInfo
				});
				if (isMounted.current) {
					setContact(data);
				}
			} catch (err) {
				toastError(err);
			}
		};

		fetchContact();
	}, [contactId, open, initialValues]);

	// ✅ FIX 05/12/2025: Escuchar eventos Socket.IO para actualizaciones de contacto en tiempo real
	useEffect(() => {
		if (!open) {
			return;
		}
		
		if (!contactId) {
			console.log("[ContactModal] ⚠️ contactId no disponible, no registrando listener");
			return;
		}
		
		if (!socketManager) {
			console.log("[ContactModal] ⚠️ socketManager no disponible, no registrando listener");
			return;
		}

		const companyId = localStorage.getItem("companyId");
		if (!companyId) {
			console.warn("[ContactModal] ⚠️ CompanyId no disponible");
			return;
		}

		const socket = socketManager.getSocket(companyId);
		if (!socket) {
			console.warn("[ContactModal] ⚠️ Socket no disponible");
			return;
		}

		// ✅ Convertir contactId a número para comparación correcta
		const contactIdNum = typeof contactId === 'string' ? parseInt(contactId, 10) : contactId;

		const handleContactUpdate = (data) => {
			if (!isMounted.current) {
				console.log("[ContactModal] ⚠️ Componente desmontado, ignorando evento");
				return;
			}

			// ✅ DEBUG: Log para ver qué se recibe
			console.log("[ContactModal] 🔔 Evento recibido:", {
				action: data.action,
				contactIdFromEvent: data.contact?.id,
				contactIdFromProps: contactId,
				contactIdNum,
				contactIdType: typeof contactId,
				eventIdType: typeof data.contact?.id,
				profilePicUrl: data.contact?.profilePicUrl
			});

			// ✅ Solo actualizar si es el contacto actual y la acción es "update"
			// ✅ Comparar tanto como número como string para mayor robustez
			const eventContactId = data.contact?.id;
			const isMatch = eventContactId === contactIdNum || 
			               eventContactId === contactId || 
			               String(eventContactId) === String(contactId) ||
			               Number(eventContactId) === Number(contactId);

			if (data.action === "update" && isMatch) {
				console.log("[ContactModal] ✅ Actualización de contacto recibida vía Socket.IO - ACTUALIZANDO:", {
					contactId: data.contact.id,
					name: data.contact.name,
					profilePicUrl: data.contact.profilePicUrl,
					updatedAt: data.contact.updatedAt
				});
				setContact(prevState => {
					const newState = {
						...prevState,
						...data.contact
					};
					console.log("[ContactModal] 📝 Nuevo estado del contacto:", newState);
					return newState;
				});
				// ✅ Forzar re-render del avatar cuando se actualiza la foto
				if (data.contact.profilePicUrl) {
					console.log("[ContactModal] 🖼️ Forzando re-render del avatar con nueva foto");
					setAvatarKey(prev => prev + 1);
				}
			} else {
				console.log("[ContactModal] ⚠️ Evento ignorado - no coincide el ID:", {
					eventContactId,
					contactId,
					contactIdNum,
					action: data.action,
					isMatch
				});
			}
		};

		// ✅ Registrar listener inmediatamente, no esperar a que el socket esté conectado
		// El socket se conectará automáticamente y el listener funcionará cuando esté listo
		const eventName = `company-${companyId}-contact`;
		console.log("[ContactModal] 📡 Registrando listener para", eventName, "contactId:", contactId, "socket.connected:", socket.connected);
		socket.on(eventName, handleContactUpdate);

		// ✅ También escuchar cuando el socket se conecta para asegurar que el listener esté activo
		const handleConnect = () => {
			console.log("[ContactModal] 🔌 Socket conectado, listener debería estar activo");
		};
		socket.on("connect", handleConnect);

		return () => {
			console.log("[ContactModal] 🧹 Limpiando listeners");
			if (socket && typeof socket.off === 'function') {
				socket.off(`company-${companyId}-contact`, handleContactUpdate);
				socket.off("connect", handleConnect);
			}
		};
	}, [open, contactId, socketManager]);

	const handleClose = () => {
		onClose();
		setContact(initialState);
		setUploadingPicture(false);
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
	};

	// ✅ NUEVO: Función para manejar selección de archivo
	const handleFileSelect = (event) => {
		const file = event.target.files[0];
		if (!file) return;

		// ✅ Validar tipo de archivo
		const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
		if (!allowedTypes.includes(file.type)) {
			toast.error("Solo se permiten archivos de imagen (JPG, PNG, WEBP, GIF)");
			return;
		}

		// ✅ Validar tamaño (máximo 5MB)
		const maxSize = 5 * 1024 * 1024; // 5MB
		if (file.size > maxSize) {
			toast.error("El archivo es demasiado grande. Máximo 5MB");
			return;
		}

		if (!contactId) {
			toast.error("Debe guardar el contacto primero antes de subir una foto");
			return;
		}

		// ✅ Crear URL temporal para la imagen
		const imageUrl = URL.createObjectURL(file);
		setSelectedImage(imageUrl);
		setCropModalOpen(true);
	};

	// ✅ NUEVO: Función para manejar recorte completo y subida
	const handleCropComplete = async (croppedImageUrl) => {
		setCropModalOpen(false);
		setUploadingPicture(true);

		try {
			// ✅ FIX 05/12/2025: Validar que la URL sea válida antes de hacer fetch
			if (!croppedImageUrl || (!croppedImageUrl.startsWith('blob:') && !croppedImageUrl.startsWith('data:'))) {
				toast.error("URL de imagen inválida. Por favor, intente nuevamente.");
				setUploadingPicture(false);
				return;
			}

			// ✅ Convertir URL a Blob con manejo de errores de CSP
			let response;
			try {
				response = await fetch(croppedImageUrl);
			} catch (fetchError) {
				console.error("Error en fetch de imagen:", fetchError);
				toast.error("El navegador bloqueó la carga de la imagen. Puede deberse a la política de seguridad (CSP) del servidor. Contacte a soporte si el problema persiste.");
				setUploadingPicture(false);
				return;
			}

			if (!response.ok) {
				throw new Error(`Error al obtener imagen: ${response.status} ${response.statusText}`);
			}

			const blob = await response.blob();
			const file = new File([blob], "profile-picture.jpg", { type: "image/jpeg" });

			// ✅ Subir imagen recortada
			const formData = new FormData();
			formData.append("picture", file);

			const { data } = await api.post(`/contacts/${contactId}/picture`, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});

			// ✅ Actualizar el contacto con la nueva foto
			setContact(prev => ({
				...prev,
				profilePicUrl: data.profilePicUrl
			}));

			// ✅ Forzar re-render del avatar inmediatamente
			setAvatarKey(prev => prev + 1);

			toast.success(data.message || "Foto de perfil actualizada exitosamente");

			// ✅ Recargar contacto desde el backend para asegurar datos actualizados
			try {
				const { data: updatedContact } = await api.get(`/contacts/${contactId}`);
				if (updatedContact) {
					setContact(updatedContact);
					// ✅ Forzar re-render del avatar nuevamente con datos actualizados
					setAvatarKey(prev => prev + 1);
				}
			} catch (reloadError) {
				console.warn("Error recargando contacto después de subir foto:", reloadError);
				// No es crítico, el estado ya se actualizó arriba
			}

			// ✅ Limpiar URL temporal
			if (selectedImage) {
				URL.revokeObjectURL(selectedImage);
			}
			setSelectedImage(null);
		} catch (err) {
			console.error("Error en handleCropComplete:", err);
			// ✅ FIX 05/12/2025: Mensaje específico para errores de CSP o red
			if (err.message && (err.message.includes('CSP') || err.message.includes('blob') || err.message.includes('fetch') || err.message.includes('blocked'))) {
				toast.error("El navegador bloqueó la carga de la imagen. Puede deberse a la política de seguridad (CSP) del servidor. Contacte a soporte si el problema persiste.");
			} else {
			toastError(err);
			}
		} finally {
			setUploadingPicture(false);
			if (fileInputRef.current) {
				fileInputRef.current.value = "";
			}
		}
	};

	const handleSaveContact = async values => {
		try {
			let response;
			if (contactId) {
				response = await api.put(`/contacts/${contactId}`, values);
				
				// ✅ CORRECCIÓN: La respuesta del PUT ahora incluye extraInfo completo
				// Usar datos de la respuesta directamente
				if (response?.data && isMounted.current) {
					setContact(response.data);
					console.log("✅ ContactModal: Contacto actualizado desde respuesta PUT:", {
						id: response.data.id,
						name: response.data.name,
						extraInfoCount: response.data.extraInfo?.length || 0,
						extraInfo: response.data.extraInfo
					});
				}
			} else {
				response = await api.post("/contacts", values);
				if (onSave && response.data) {
					onSave(response.data);
				}
			}

			if (contactId && onSave && response?.data) {
				onSave(response.data);
			}
			
			// ✅ CORREGIR: Mostrar mensaje informativo si el número no está en WhatsApp
			if (response?.data?.warning === "NUMBER_NOT_IN_WHATSAPP") {
				toast.warning(
					response.data.message || 
					i18n.t("backendErrors.NUMBER_NOT_IN_WHATSAPP") ||
					"El contacto se guardó exitosamente, pero el número no está registrado en WhatsApp.",
					{ autoClose: 5000 }
				);
			} else {
				toast.success(i18n.t("contactModal.success"));
			}
			
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	return (
		<div className={classes.root}>
			<Dialog open={open} onClose={handleClose} maxWidth="lg" scroll="paper">
				<DialogTitle id="form-dialog-title">
					{contactId
						? `${i18n.t("contactModal.title.edit")}`
						: `${i18n.t("contactModal.title.add")}`}
				</DialogTitle>
				{/* ✅ NUEVO: Avatar con foto de perfil o iniciales */}
				{contactId && !cropModalOpen && (
					<Box className={classes.avatarContainer}>
						<Box position="relative" display="inline-block">
							<Avatar
								key={avatarKey} // ✅ NUEVO: Key para forzar re-render cuando cambia la foto
								className={`${classes.avatar}${!(contact?.profilePicUrl && contact.profilePicUrl !== "" && !contact.profilePicUrl.includes("nopicture.png")) ? " avatar-no-photo" : ""}`}
								style={{
									width: 100,
									height: 100,
									minWidth: 100,
									minHeight: 100,
									backgroundColor: generateColor(contact?.number || contact?.name || ""),
									fontWeight: "bold",
									color: "white",
									fontSize: "2.5rem",
									lineHeight: "100px",
								}}
								imgProps={{
									style: {
										objectFit: "cover",
										width: "100%",
										height: "100%",
										display: "block"
									},
									onError: (e) => {
										// ✅ FIX 05/12/2025: Si la imagen falla al cargar, ocultar para mostrar iniciales
										if (e.target) {
											e.target.style.display = 'none';
										}
									},
									onLoad: (e) => {
										// ✅ FIX 05/12/2025: Asegurar que la imagen se muestre correctamente
										if (e.target) {
											e.target.style.display = 'block';
										}
									}
								}}
								src={
									contact?.profilePicUrl &&
									contact.profilePicUrl !== "" &&
									!contact.profilePicUrl.includes("nopicture.png")
										? `${getProfilePicUrl(contact.profilePicUrl)}?t=${avatarKey}`
										: null
								}
								onError={(e) => {
									// ✅ FIX 05/12/2025: Si la imagen falla al cargar, ocultar para mostrar iniciales
									if (e.target) {
										e.target.style.display = 'none';
									}
								}}
							>
								{contact?.profilePicUrl &&
								 contact.profilePicUrl !== "" &&
								 !contact.profilePicUrl.includes("nopicture.png")
									? null // ✅ FIX 05/12/2025: No mostrar iniciales si hay imagen válida
									: getInitials(contact?.name || "")}
							</Avatar>
							<IconButton
								className={classes.cameraIcon}
								size="small"
								onClick={() => fileInputRef.current?.click()}
								disabled={uploadingPicture}
							>
								{uploadingPicture ? (
									<CircularProgress size={16} />
								) : (
									<CameraAltIcon fontSize="small" />
								)}
							</IconButton>
						</Box>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							onChange={handleFileSelect}
							className={classes.hiddenInput}
						/>
					</Box>
				)}
				<Formik
					key={contactId ? `contact-${contactId}` : "new-contact"}
					initialValues={initialValues || contact}
					enableReinitialize={true}
					validationSchema={ContactSchema}
					onSubmit={async (values, actions) => {
						try {
							await handleSaveContact(values);
						} finally {
							actions.setSubmitting(false);
						}
					}}
				>
					{({ values, errors, touched, isSubmitting, setFieldValue }) => {
						// console.log("🔍 Contact - Formik render - values:", values); // ✅ LOG REMOVIDO PARA PRODUCCIÓN
						return (
						<Form>
							<DialogContent dividers>
								<Typography variant="subtitle1" gutterBottom>
									{i18n.t("contactModal.form.mainInfo")}
								</Typography>
								<TextField
									label={i18n.t("contactModal.form.name")}
									name="name"
									autoFocus
									value={values.name || ""}
									onChange={(e) => setFieldValue('name', e.target.value)}
									error={touched.name && Boolean(errors.name)}
									helperText={touched.name && errors.name}
									variant="outlined"
									margin="dense"
									className={classes.textField}
								/>
								<TextField
									label={i18n.t("contactModal.form.number")}
									name="number"
									value={values.number || ""}
									onChange={(e) => setFieldValue('number', e.target.value)}
									error={touched.number && Boolean(errors.number)}
									helperText={touched.number && errors.number}
									placeholder="5541998608485"
									variant="outlined"
									margin="dense"
								/>
								<div>
									<TextField
										label={i18n.t("contactModal.form.email")}
										name="email"
										value={values.email || ""}
										onChange={(e) => setFieldValue('email', e.target.value)}
										error={touched.email && Boolean(errors.email)}
										helperText={touched.email && errors.email}
										placeholder="Email address"
										fullWidth
										margin="dense"
										variant="outlined"
									/>
								</div>
								{contactId && contact?.whatsapp?.name ? (
									<TextField
										label={i18n.t("contactModal.form.whatsapp")}
										value={contact.whatsapp.name}
										disabled
										fullWidth
										margin="dense"
										variant="outlined"
										helperText={i18n.t("contactModal.form.whatsappHelper")}
									/>
								) : null}
								<Typography
									style={{ marginBottom: 8, marginTop: 12 }}
									variant="subtitle1"
								>
									{i18n.t("contactModal.form.extraInfo")}
								</Typography>

								<FieldArray name="extraInfo">
									{({ push, remove }) => (
										<>
											{values.extraInfo &&
												values.extraInfo.length > 0 &&
												values.extraInfo.map((info, index) => (
													<div
														className={classes.extraAttr}
														key={`${index}-info`}
													>
														<TextField
															label={i18n.t("contactModal.form.extraName")}
															name={`extraInfo[${index}].name`}
															value={values.extraInfo?.[index]?.name || ""}
															onChange={(e) => setFieldValue(`extraInfo[${index}].name`, e.target.value)}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<TextField
															label={i18n.t("contactModal.form.extraValue")}
															name={`extraInfo[${index}].value`}
															value={values.extraInfo?.[index]?.value || ""}
															onChange={(e) => setFieldValue(`extraInfo[${index}].value`, e.target.value)}
															variant="outlined"
															margin="dense"
															className={classes.textField}
														/>
														<IconButton
															size="small"
															onClick={() => remove(index)}
														>
															<DeleteOutlineIcon />
														</IconButton>
													</div>
												))}
											<div className={classes.extraAttr}>
												<Button
													style={{ flex: 1, marginTop: 8 }}
													variant="outlined"
													color="primary"
													onClick={() => push({ name: "", value: "" })}
												>
													{`+ ${i18n.t("contactModal.buttons.addExtraInfo")}`}
												</Button>
											</div>
										</>
									)}
								</FieldArray>
								{/* ✅ NUEVO: Sección de Observaciones del Contacto */}
								{contactId && (
									<ContactNotesStandalone contactId={contactId} />
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("contactModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{contactId
										? `${i18n.t("contactModal.buttons.okEdit")}`
										: `${i18n.t("contactModal.buttons.okAdd")}`}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
						);
					}}
				</Formik>
			</Dialog>
			{/* ✅ NUEVO: Modal de recorte de imagen */}
			<ImageCropModal
				open={cropModalOpen}
				onClose={() => {
					setCropModalOpen(false);
					if (selectedImage) {
						URL.revokeObjectURL(selectedImage);
						setSelectedImage(null);
					}
				}}
				imageSrc={selectedImage}
				onCropComplete={handleCropComplete}
				aspect={1}
				shape="round"
			/>
		</div>
	);
};

export default ContactModal;
